import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const Footer = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [status, setStatus] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');

    try {
      const response = await fetch('https://formspree.io/f/xdalngqo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: formData.message,
          _replyto: formData.email,
          _subject: `New Contact from ${formData.name} - Vesta Designs Website`,
        }),
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setStatus(''), 5000);
      } else {
        // Fallback to mailto if Formspree not configured
        const mailtoLink = `mailto:director@vestadesigns.com.au?subject=New Contact from ${encodeURIComponent(formData.name)}&body=${encodeURIComponent(`Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`)}`;
        window.location.href = mailtoLink;
        setStatus('');
      }
    } catch (error) {
      // Fallback to mailto
      const mailtoLink = `mailto:director@vestadesigns.com.au?subject=New Contact from ${encodeURIComponent(formData.name)}&body=${encodeURIComponent(`Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`)}`;
      window.location.href = mailtoLink;
      setStatus('');
    }
  };

  return (
    <footer className="footer-arch04">
      {/* Let's Talk Section - Clean Full Width */}
      <div className="footer-cta-section">
        <motion.div 
          className="footer-cta-content"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="footer-cta-title">Let's Talk</h2>
          <p className="footer-cta-text">
            Ready to transform your space? Get in touch and let's create something extraordinary together.
          </p>
        </motion.div>
        <motion.div 
          className="footer-cta-form"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
        >
          <form className="lets-talk-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-field">
                <input 
                  type="text" 
                  name="name"
                  placeholder="Name*" 
                  value={formData.name}
                  onChange={handleChange}
                  required 
                />
              </div>
              <div className="form-field">
                <input 
                  type="email" 
                  name="email"
                  placeholder="Email address*" 
                  value={formData.email}
                  onChange={handleChange}
                  required 
                />
              </div>
            </div>
            <div className="form-field">
              <textarea 
                name="message"
                placeholder="Your message" 
                rows="4"
                value={formData.message}
                onChange={handleChange}
              ></textarea>
            </div>
            <button type="submit" className="btn-send-message" disabled={status === 'sending'}>
              {status === 'sending' ? 'Sending...' : 'Send message'}
            </button>
            {status === 'success' && (
              <p className="form-status success">Message sent successfully!</p>
            )}
            {status === 'error' && (
              <p className="form-status error">Failed to send. Please try again.</p>
            )}
          </form>
        </motion.div>
      </div>

      {/* Footer Main */}
      <div className="footer-main">
        <div className="footer-container">
          <div className="footer-grid">
            {/* Contact Info */}
            <div className="footer-col footer-contact">
              <h3 className="footer-heading">Head office</h3>
              <ul className="footer-list">
                <li>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                  Level 2, 260 Collins Street, Melbourne VIC 3000
                </li>
                <li>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                  <a href="tel:+61387230159">(03) 8723 0159</a>
                </li>
                <li>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                  <a href="mailto:director@vestadesigns.com.au">director@vestadesigns.com.au</a>
                </li>
              </ul>
            </div>

            {/* Company Links */}
            <div className="footer-col">
              <h3 className="footer-heading">Company</h3>
              <ul className="footer-links">
                <li><Link to="/portfolio">Works</Link></li>
                <li><Link to="/login">Login</Link></li>
                {/* HIDDEN: Team link temporarily hidden - uncomment to restore */}
                {/* <li><Link to="/about">Meet Our Team</Link></li> */}
                <li><Link to="/contact">Contact</Link></li>
              </ul>
            </div>

            {/* Services Links */}
            <div className="footer-col">
              <h3 className="footer-heading">Services</h3>
              <ul className="footer-links">
                <li><Link to="/services">Residential Design</Link></li>
                <li><Link to="/services">Town Planning</Link></li>
                <li><Link to="/services">3D Visualization</Link></li>
                <li><Link to="/services">Pre-Purchase Advice</Link></li>
                <li><Link to="/services">Interior Design</Link></li>
              </ul>
            </div>

            {/* Social */}
            <div className="footer-col footer-social-col">
              <h3 className="footer-heading">Follow us</h3>
              <div className="footer-social">
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                  </svg>
                </a>
                <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer Bottom */}
      <div className="footer-bottom">
        <div className="footer-container">
          <div className="footer-bottom-content">
            <Link to="/" className="footer-logo">
              <img 
                src="/vesta-logo-white.png"
                alt="Vesta Designs"
              />
            </Link>
            <p className="footer-copyright">
              © Copyright, Vesta Designs 2025
            </p>
            <div className="footer-legal">
              <Link to="/terms">Terms & Conditions</Link>
              <Link to="/privacy">Privacy Policy</Link>
              <Link to="/support">Support Center</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
